import { useState, useEffect, useRef, useCallback, useMemo} from 'react';
import ReactMarkdown from 'react-markdown';
import remarkBreaks from 'remark-breaks';
import autosize from "autosize";
import remarkGfm from 'remark-gfm'
import CheckIcon from "../images/icons/white/check.svg";
import { useDispatch } from 'react-redux';
import { buildFormDataFromObject } from '../utils/commonUtils';
import { alertActions, content_groupActions, contentActions } from "../actions";
import { CKEditor } from '@ckeditor/ckeditor5-react';
import BalloonEditor from '@ckeditor/ckeditor5-build-balloon';
import rehypeRaw from "rehype-raw";
import { markdownToHtml } from '../utils/markdownToHtml';
import { Button } from 'react-bootstrap';
import { htmlToFacebookText } from '../utils/useTextFormatter';

function ContentInlineEdit(props) {
  const {
    content_data,
     content_attachment_type,
     inline_type,
     content_height,
     hasContents,
     workspace_id,
     content_group_id,
     setEditorFocused,
     content_popup_notification
  } = props
  const dispatch = useDispatch();
  const [ contentValue, setContentValue] = useState(content_data && content_data);
  const [ editMode, setEditMod] = useState(false);
  const [file, setFile] = useState(null);
  const [submitDisabled, setSubmitDisabled] = useState(false);
  const [waitingClick, setWaitingClick] = useState(null);
  const [lastClick, setLastClick] = useState(0);
  const previousDataRef = useRef('');
  const editorRef = useRef();

  const ckeditorConfig = {
    toolbar: {
      items: [
          'undo', 'redo',
          '|',
          'heading',
          '|',
          'fontfamily', 'fontsize', 'fontColor', 'fontBackgroundColor',
          '|',
          'bold', 'italic', 'strikethrough', 'subscript', 'superscript', 'code',
          '|',
          'link', 'blockQuote', 'codeBlock', 'insertTable', 
          '|',
          'bulletedList', 'numberedList', 'outdent', 'indent'
      ],
      shouldNotGroupWhenFull: false
    },
    removePlugins: ['ImageInsert'],
    resize_enabled: true,
    disableNativeSpellChecker: false,
    link: {
        defaultProtocol: 'https://',
        addTargetToExternalLinks: true
    },
    placeholder: 'Add description...',
  }

  const input = useRef(null);
  const editorEnd = useRef(null);

  let ignoreEdit = false;

  useEffect(() => {
    setContentValue(content_data)
    //setEditMod(!content_data)
  }, [content_data]);

    useEffect(() => {
    if (editMode && editorRef.current && inline_type === 'content_description') {
        setEditorFocused?.(true);
        setTimeout(() => {
          const editorInstance = editorRef.current.editor;
          if (editorInstance && !editorInstance.isReadOnly) {
            editorInstance.editing.view.focus();
          }
        }, 50);
    }

    if(editMode === false){
      setEditorFocused?.(false);
    }
  }, [editMode]);

  const scrollToBottom = () => {
    if (editorEnd.current) {
      editorEnd.current.scrollIntoView({ behavior: "smooth", block: "end", inline: "nearest" });
    }
  }

  const processClick = (e) => {
    if(lastClick &&e.timeStamp - lastClick < 250 && waitingClick){
      //Double Click

      setLastClick(0);
      clearTimeout(waitingClick);
      setWaitingClick(null);
      handleEnableEditMode();

    }
    else{
      //Single Click

      setLastClick(e.timeStamp);
      setWaitingClick(setTimeout(()=>{
      setWaitingClick(null);
      e.preventDefault();
      }, 251))
    }
  }

  const onKeyDown = (event) => {
    if (inline_type === 'content_description') {
      return;
    }

    if (event.key === "Escape") {
      setEditMod(false);
      setContentValue(content_data);

      event.preventDefault();
      event.stopPropagation();
      return false;
    }

    if (event.key === "Enter") {
      if (!editMode) {
        return;
      }

      event.target.blur();
    }
  }

  const handleEnableEditMode = useCallback(() => {
    setEditMod(true)
    if (inline_type !== 'content_description') {
      setTimeout(() => {
        input.current.focus();
        scrollToBottom();
      }, 50);
    }
  }, [input]);


  const handleOnBlur = (e) => {
    if (!editMode) {
      return;
    }
    props.handleContentInlineEdit({title: e.target.value})
  }

  const handleInputOnChange = (event) => {
    setContentValue(event.target.value)
  }

  const handleCkeditorChange = (event, editor) => {
    const data = editor.getData();
    setContentValue(data);
  }

  const setFocused = (e) => {
    e.target.select();
    e.target.classList.add("focus_edit_input");
  }

  const handleFileChange = e => {
    setContentValue(URL.createObjectURL(e.target.files[0]));
    props.handleContentInlineEdit({attachment: e.target.files[0]})
  }

  const handleSubmit = (e) => {
    let from_content_popup_notif = false;
    let handleOnBlur = null;
    let formData = new FormData();

    let values = { description: contentValue };

    buildFormDataFromObject(formData, values, "content_group");

    const onSuccess = (res) => {
      setEditMod(false);
    }

    dispatch(content_groupActions.inlineEditContentGroup(handleOnBlur, 
                                                         formData, 
                                                         content_group_id, 
                                                         workspace_id, 
                                                         from_content_popup_notif, 
                                                         setSubmitDisabled, 
                                                         {
                                                          onSuccess
                                                         }
                                                        ));
  }

  const handleCancel = () => {
    if(content_data === contentValue) {
      setEditMod(false)
    } else {
      setContentValue(content_data || '');
    }
  }

   const handleConvert = async () => {
     try {
      await navigator.clipboard.writeText(htmlToFacebookText(contentValue));
      dispatch(alertActions.success('Copied'))
    } catch (err) {
      dispatch(alertActions.error('Failed to copy'))
    }
  };

  const hiddenFileInput = useRef(null);

  const submitButtonStatus = (input.current && ((input.current.value === '') || content_data === input.current.value));

  const renderedMarkdown = useMemo(() => {
    return <ReactMarkdown
                remarkPlugins={[remarkGfm, remarkBreaks]}
                components={{
                  a: props => {
                    return (
                      <a href={props.href} onClick={(e) => { e.stopPropagation(); return true; }}className="link_color" target="_blank">{props.children}</a>
                    )
                  }
                }}
                rehypePlugins={[rehypeRaw]}
            >
            {content_data ? content_data : 'Add description...'}
          </ReactMarkdown>
  }, [content_data]);
  

  const handleEditorReady = (editor) => {
    const undoCommand = editor.commands.get('undo');
    const redoCommand = editor.commands.get('redo');

    const getCurrentText = () => editor.getData();

    const captureChange = (type) => {
      const value = getCurrentText();
      setContentValue(value);
    };

    // Set the initial content
    previousDataRef.current = getCurrentText();

    undoCommand.on('execute', () => {
      setTimeout(() => captureChange('Undo'), 0);
    });

    redoCommand.on('execute', () => {
      setTimeout(() => captureChange('Redo'), 0);
    });

    editorRef.current = { editor };
    if (editMode) {
      editor.editing.view.focus();
    }
  }

  const render_component = () => {
    if(inline_type === "content_title") {
      return(
        editMode === false ?
          <h5 onClick={handleEnableEditMode} className="mb-2 title-top">
            {contentValue}
          </h5>
        :
          <div style={{position:'relative'}} onBlur={(e) => handleOnBlur(e)}>
            <input
              type="text"
              ref={input}
              value={contentValue}
              className="form-control inline-edit-border"
              onKeyDown={onKeyDown}
              placeholder="Add Title"
              onChange={handleInputOnChange}
              onFocus={ setFocused }
            />
          </div>
      )
    }else if(inline_type === "file_upload") {
      return (
        content_data &&
          <div className="file_upload_content">
            <input type="file" onChange={handleFileChange} ref={hiddenFileInput} hidden/>
            {content_data &&
              <a href={ content_data } target="_blank">
                { content_attachment_type == "application/pdf" ?
                  <i className="fa fa-file-pdf-o mr-2 big-icon" aria-hidden="true"></i>
                :
                  <img src={contentValue} alt="content" style={{ minHeight: "" + content_height + "px", maxHeight: "" + content_height + "px" }} />
                }
              </a>
            }
          </div>
      )
    }else {
      return(
        <div
          className={''}
        >
          {
            content_data && 

                      <Button 
            size='sm' 
            variant='outline-gray' 
            onClick={handleConvert} 
            className='mb-2 mt-4 form-control p-2 btn-copy-description'
          >
              Copy Description
          </Button>
          }

          {
            !content_data && editMode === false ?
              <div
                onClick={processClick}
                className={`btn btn-dashed btn-sm form-control p-2 ${ content_data ? '' : 'empty-description mt-4' }`}
              >
                {renderedMarkdown}
              </div>
            :
              <div style={{ position: "relative" }} >
                <div
                  onClick={processClick}
                >
                  <CKEditor
                    editor={BalloonEditor}
                    config={ckeditorConfig}
                    rows={8}
                    ref={input}
                    className="form-control-custom"
                    data={markdownToHtml(contentValue) || ''}
                    placeholder="Add text"
                    onChange={handleCkeditorChange}
                    onKeyDown={onKeyDown}
                    onReady={handleEditorReady}
                    style={{ maxHeight: 'none' }}
                    disabled={editMode ? false : true}
                  />
                </div>
                {
                  editMode && (
                    <div className="button-group description_button-group text-right">
                      <input
                        type="submit"
                        className="btn btn-outline-gray btn-sm"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={handleCancel}
                        value="Cancel"
                        disabled={submitButtonStatus}
                      />
                      <button
                        type="submit"
                        className="btn btn-primary btn-sm"
                        onClick={handleSubmit}
                        disabled={submitButtonStatus}
                      >
                        Save
                      </button>
                  </div>
                  )
                }

                <div ref={ editorEnd } className="description-section__bottom" />
              </div>
          }
        </div>
      )
    }
  }

  return (
    <div className="content_inline_edit mb-4">
      {render_component()}
    </div>
  );
}

export { ContentInlineEdit };
